import { invertGraphDiff, isEmptyGraphDiff } from './graphDiff';
import type { GraphDiff } from './graphDiff';

/** How many steps undo can go back (D-036). */
export const DEFAULT_HISTORY_LIMIT = 100;

/** `past`: done actions, oldest first. `future`: undone actions, next redo first. */
export type History = { past: readonly GraphDiff[]; future: readonly GraphDiff[] };

export const EMPTY_HISTORY: History = Object.freeze({ past: [], future: [] });

/** One history step: the diff to apply to the graph and the history after it. */
export type HistoryStep = { history: History; diff: GraphDiff };

/** Records an action; a new action drops whatever could be redone. Empty diffs are not recorded. */
export const pushHistory = (history: History, diff: GraphDiff, limit = DEFAULT_HISTORY_LIMIT): History =>
  isEmptyGraphDiff(diff) ? history : { past: [...history.past, diff].slice(-limit), future: [] };

/** Steps back: applies the inverse of the last action. `null` when there is nothing to undo. */
export const undoHistory = ({ past, future }: History): HistoryStep | null => {
  if (past.length === 0) return null;
  const last = past[past.length - 1];
  return { history: { past: past.slice(0, -1), future: [last, ...future] }, diff: invertGraphDiff(last) };
};

/** Steps forward again: re-applies the last undone action. `null` when there is nothing to redo. */
export const redoHistory = ({ past, future }: History): HistoryStep | null => {
  if (future.length === 0) return null;
  const [next, ...rest] = future;
  return { history: { past: [...past, next], future: rest }, diff: next };
};
